/**
 * User Profile Page
 * Display user information and accessible tasks
 */

import React, { useEffect, useState } from "react";
import { usePageTitle } from "../contexts/PageTitleContext";
import {
  ApplicationPermissionId,
  HasModulePermission,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import { getFullPath } from "../utils/getFullPath";
import { usersService } from "../services/api/users.service";
import type { ApiUser } from "../types/user.types";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import "../styles/pages/UserProfile.css";

export const UserProfile: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState<ApiUser | null>(null);

  const fallbackFirstName = localStorage.getItem("twdc_first_name") ?? "User";
  const fallbackLastName = localStorage.getItem("twdc_last_name") ?? "";
  const fallbackMiddleName = localStorage.getItem("twdc_middle_name") ?? "";
  const fallbackUsername = localStorage.getItem("twdc_username") ?? "";
  const fallbackEmail = localStorage.getItem("twdc_email") ?? "";
  const roleName = localStorage.getItem("twdc_role_name") ?? "Role";
  const fallbackUserImage = localStorage.getItem("twdc_user_image");

  const resolveUserId = (): number | null => {
    const storedId = localStorage.getItem("twdc_user_id");
    if (storedId && !Number.isNaN(Number(storedId))) {
      return Number(storedId);
    }

    const accessToken = localStorage.getItem("twdc_access_token");
    if (!accessToken) return null;

    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      const tokenUserId =
        payload.userId ||
        payload.uid ||
        payload.nameid ||
        payload.sub ||
        payload[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
        ];

      if (tokenUserId && !Number.isNaN(Number(tokenUserId))) {
        return Number(tokenUserId);
      }
    } catch {
      return null;
    }

    return null;
  };

  const mapGender = (genderId?: number): string => {
    if (genderId === 1) return "Male";
    if (genderId === 2) return "Female";
    return "N/A";
  };

  const formatDateOfBirth = (value?: string): string => {
    if (!value) return "N/A";
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;
    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const firstName = userData?.firstName || fallbackFirstName;
  const lastName = userData?.lastName || fallbackLastName;
  const middleName = userData?.middleName || fallbackMiddleName;
  const username = userData?.username || fallbackUsername;
  const email = userData?.email || fallbackEmail;
  const rawUserImage = userData?.profileImagePath || fallbackUserImage;
  const userImage = getFullPath(rawUserImage);
  const uid =
    userData?.uid ||
    userData?.userNo ||
    String(
      userData?.id ||
        localStorage.getItem("twdc_uid") ||
        localStorage.getItem("twdc_user_id") ||
        "N/A",
    );
  const gender = userData
    ? mapGender(userData.genderId)
    : localStorage.getItem("twdc_gender") || "N/A";
  const dateOfBirth = userData
    ? formatDateOfBirth(userData.dateOfBirth)
    : localStorage.getItem("twdc_date_of_birth") ||
      localStorage.getItem("twdc_dob") ||
      "N/A";
  const nationalId =
    userData?.nationalIdNumber ||
    localStorage.getItem("twdc_national_id") ||
    localStorage.getItem("twdc_national_id_number") ||
    "N/A";
  const accountStatus =
    userData?.statusName ||
    userData?.status?.name ||
    localStorage.getItem("twdc_status") ||
    "CREATED";
  const displayName = `${firstName} ${middleName} ${lastName}`.trim();
  const usernameOrEmail = email || username || "N/A";

  const hasAnySidebarMenuPermission =
    HasPermission(ModuleId.Dashboard, ApplicationPermissionId.View) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Apply) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Approve) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Print) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.Reprint) ||
    HasPermission(ModuleId.Weddings, ApplicationPermissionId.View) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Apply) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Approve) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Print) ||
    HasPermission(ModuleId.Divorces, ApplicationPermissionId.Reprint) ||
    HasModulePermission(ModuleId.Users) ||
    HasModulePermission(ModuleId.Roles) ||
    HasModulePermission(ModuleId.Workflow) ||
    HasModulePermission(ModuleId.MasterData) ||
    HasModulePermission(ModuleId.AuditLogs) ||
    HasModulePermission(ModuleId.Settings);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop && hasAnySidebarMenuPermission);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView && hasAnySidebarMenuPermission);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasAnySidebarMenuPermission]);

  useEffect(() => {
    setPageTitle("My Profile", "View your profile and accessible features");
  }, [setPageTitle]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const userId = resolveUserId();
      if (!userId) return;

      try {
        const response = await usersService.getUserById(userId);
        setUserData(response);
      } catch {
        setUserData(null);
      }
    };

    fetchUserProfile();
  }, []);

  return (
    <div
      className={`dashboard-body ${hasAnySidebarMenuPermission && sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() =>
          hasAnySidebarMenuPermission && setSidebarOpen((prev) => !prev)
        }
        sidebarOpen={sidebarOpen}
      />
      {hasAnySidebarMenuPermission && <Sidebar isOpen={sidebarOpen} />}
      <main className="main-content">
        <div className="profile-shell">
          <section className="profile-card">
            <div className="profile-card-top">
              <div className="profile-card-avatar">
                {userImage ? (
                  <img
                    src={userImage || undefined}
                    alt={displayName || "User"}
                  />
                ) : (
                  <span className="avatar-initials">
                    {firstName.charAt(0)}
                    {lastName.charAt(0)}
                  </span>
                )}
              </div>
              <div className="profile-card-identity">
                <h1 className="profile-card-name">{displayName || "User"}</h1>
                <div className="profile-card-meta">
                  <span>@{usernameOrEmail}</span>
                  <span>•</span>
                  <span>{roleName}</span>
                  <span>•</span>
                  <span className="profile-status-badge">{accountStatus}</span>
                </div>
              </div>
            </div>

            <div className="profile-section-title">Personal Information</div>

            <div className="profile-info-grid">
              <div className="profile-info-item">
                <div className="profile-info-label">UID</div>
                <div className="profile-info-value">{uid}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">Username</div>
                <div className="profile-info-value">{usernameOrEmail}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">First Name</div>
                <div className="profile-info-value">{firstName || "N/A"}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">Middle Name</div>
                <div className="profile-info-value">{middleName || "N/A"}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">Last Name</div>
                <div className="profile-info-value">{lastName || "N/A"}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">Gender</div>
                <div className="profile-info-value">{gender}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">Date Of Birth</div>
                <div className="profile-info-value">{dateOfBirth}</div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-label">National ID</div>
                <div className="profile-info-value">{nationalId}</div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
